import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Button,
  Typography,
  LinearProgress,
  Snackbar,
  TextField,
  Box
} from '@mui/material';
import { API_BASE_URL } from 'src/apiConfig';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LoadingOverlay } from 'src/scenes/students/administration/helper/const';
import ConfirmDeleteDialog from 'src/components/dialogs/delete/ConfirmDeleteDialog';

const SchoolsWithWorks = () => {
  const { t } = useTranslation();
  const [schools, setSchools] = useState([]);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [searchId, setSearchId] = useState('');
  const [showFullyConnected, setShowFullyConnected] = useState(false); // Filter toggle

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedSchoolId, setSelectedSchoolId] = useState(null);
  const [totalSchoolsWithStudents, setTotalSchoolsWithStudents] = useState(0);

  useEffect(() => {
    fetchSchools();
    fetchTotalSchoolsWithStudents();
  }, []);

  const fetchSchools = () => {
    setIsLoading(true);
    fetch(`${API_BASE_URL}/api/connectingWork.php?action=getNotConnectWork`)
      .then((response) => response.json())
      .then((data) => setSchools(data))
      .catch((error) => console.error('Error fetching data:', error))
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchTotalSchoolsWithStudents = () => {
    fetch(`${API_BASE_URL}/api/schools.php?totalCountWithStudents`)
      .then((response) => response.json())
      .then((data) => setTotalSchoolsWithStudents(data.totalCountWithStudents))
      .catch((error) => console.error('Error fetching total count:', error));
  };

  const handleConnectClick = (schoolId) => {
    navigate(`/connect-work/${schoolId}`);
  };

  const handleDeleteClick = (schoolId) => {
    setSelectedSchoolId(schoolId);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);
    fetch(
      `${API_BASE_URL}/api/reviewAll.php?action=delete&schoolId=${selectedSchoolId}`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 'success') {
          fetchSchools();
          setSnackbarMessage(t('dialog.successfullyDeleted'));
          setSnackbarOpen(true);
        } else {
          setSnackbarMessage(data.error);
          setSnackbarOpen(true);
          console.log(data.error || t('dialog.deleteFailed'));
        }
      })
      .catch((error) => {
        console.error('Error deleting works:', error);
        alert(t('dialog.deleteFailed'));
      })
      .finally(() => {
        setIsLoading(false);
        setConfirmDialogOpen(false);
        setSelectedSchoolId(null);
      });
  };

  const handleCloseDeleteDialog = () => {
    setConfirmDialogOpen(false);
    setSelectedSchoolId(null);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const toggleFullyConnectedFilter = () => {
    setShowFullyConnected((prev) => !prev);
  };

  const columns = [
    { field: 'id', headerName: t('review.id'), width: 90 },
    { field: 'name', headerName: t('review.school_name'), width: 310 },
    { field: 'zipcode', headerName: t('location.zipcode'), width: 120 },
    { field: 'email', headerName: t('profile.email'), width: 250 },
    {
      field: 'totalStudentCountDB',
      headerName: t('review.student_count'),
      width: 150
    },
    {
      field: 'totalJpg',
      headerName: t('review.scanned_works_count'),
      width: 160
    },
    {
      field: 'studentCountConnect',
      headerName: t('review.connected_works_count'),
      width: 200
    },
    {
      field: 'action',
      headerName: t('review.action'),
      width: 300,
      renderCell: (params) => (
        <div>
          <Button
            variant="contained"
            color="primary"
            aria-hidden={false}
            onClick={() => handleConnectClick(params.row.id)}
            style={{
              backgroundColor: '#4cceac',
              color: '#fff',
              fontWeight: 'bold',
              textTransform: 'none',
              borderRadius: '8px'
            }}
          >
            {t('review.connect')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            aria-hidden={false}
            onClick={() => handleDeleteClick(params.row.id)}
            style={{
              backgroundColor: '#e57373',
              color: '#fff',
              fontWeight: 'bold',
              textTransform: 'none',
              borderRadius: '8px',
              marginLeft: '10px'
            }}
          >
            {t('review.deleteReview')}
          </Button>
        </div>
      )
    }
  ];

  const rows = schools.map((school) => ({
    id: school.id,
    name: school.name,
    zipcode: school.zipcode,
    email: school.email,
    totalStudentCountDB: Number(school.totalStudentCountDB),
    totalJpg: Number(school.totalJpg),
    studentCountConnect: Number(school.studentCountConnect)
  }));

  const filteredRows = rows.filter((row) => {
    const idMatch = searchId ? row.id.toString().includes(searchId) : true;
    const fullyConnectedMatch = showFullyConnected
      ? row.totalStudentCountDB > 0 &&
        row.studentCountConnect === row.totalStudentCountDB
      : true;
    return idMatch && fullyConnectedMatch;
  });

  return (
    <div
      style={{
        height: 600,
        width: '100%',
        padding: '10px',
        borderRadius: '16px'
      }}
    >
      {isLoading && <LinearProgress />}

      <Typography
        variant="body1"
        sx={{ color: '#0d47a1', fontWeight: 'bold', marginBottom: '5px' }}
      >
        {t('review.currentlyScanned', { count: schools.length })}
      </Typography>
      <Typography
        variant="body1"
        sx={{ color: '#0d47a1', fontWeight: 'bold', marginBottom: '5px' }}
      >
        {t('review.totalRegisteredSchools', {
          count: totalSchoolsWithStudents
        })}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          marginBottom: '10px'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
          {t('review.searchById')}
        </Typography>
        <TextField
          variant="outlined"
          size="small"
          placeholder={t('review.id')}
          value={searchId}
          onChange={(e) => setSearchId(e.target.value)}
          sx={{
            width: '200px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px'
          }}
        />

        <Button
          variant={showFullyConnected ? 'contained' : 'outlined'}
          onClick={toggleFullyConnectedFilter}
          sx={{
            backgroundColor: showFullyConnected ? '#4cceac' : 'transparent',
            color: showFullyConnected ? '#fff' : '#0d47a1',
            fontWeight: 'bold',
            border: `2px solid ${showFullyConnected ? '#4cceac' : '#0d47a1'}`,
            textTransform: 'none',
            borderRadius: '8px',
            padding: '5px 15px'
          }}
        >
          {showFullyConnected
            ? t('review.showAll')
            : t('review.showFullyConnected')}
        </Button>
      </Box>

      <DataGrid
        rows={filteredRows}
        columns={columns}
        pageSize={7}
        rowsPerPageOptions={[5, 10, 20]}
        checkboxSelection={false}
        loading={isLoading}
        components={{
          LoadingOverlay: LoadingOverlay
        }}
        disableColumnMenu
        componentsProps={{
          pagination: {
            labelRowsPerPage: t('pagination.rowsPerPage'),
            labelDisplayedRows: ({ from, to, count }) =>
              `${from}-${to} ${t('pagination.of')} ${
                count !== -1 ? count : `${t('pagination.moreThan')} ${to}`
              }`
          }
        }}
        sx={{
          boxShadow: 2,
          borderRadius: '16px',
          backgroundColor: '#ffffff',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#3788d8',
            color: '#fff',
            fontSize: '16px',
            fontWeight: 'bold',
            borderBottom: '2px solid #ddd'
          },
          '& .MuiDataGrid-cell': {
            color: '#0d47a1',
            fontSize: '14px'
          },
          '& .MuiDataGrid-footerContainer': {
            backgroundColor: '#e3f2fd',
            borderTop: '1px solid #ddd'
          }
        }}
      />

      <ConfirmDeleteDialog
        open={confirmDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </div>
  );
};

export default SchoolsWithWorks;
